/**
 * 단순화된 PIN 관리 서비스
 * Master PIN만 사용하는 통합 시스템
 * 개별 항목 PIN 제거하여 복잡도 감소
 */

import { doc, updateDoc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { hashPin, verifyPin } from './pinService';

const USER_SETTINGS_COLLECTION = 'userSettings';
const SECURE_NOTES_COLLECTION = 'secureNotes';
const SECURE_FILES_COLLECTION = 'secureFiles';

/**
 * 마스터 PIN 설정
 */
export async function setMasterPin(userId, pin) {
  try {
    const { hash, salt } = await hashPin(pin);
    
    const userSettingsRef = doc(db, USER_SETTINGS_COLLECTION, userId);
    await setDoc(userSettingsRef, {
      masterPin: {
        hash,
        salt,
        enabled: true,
        createdAt: new Date(),
        updatedAt: new Date()
      }
    }, { merge: true });
    
    return true;
  } catch (error) {
    console.error('Error setting master PIN:', error);
    throw error;
  }
}

/**
 * 마스터 PIN 검증
 */
export async function verifyMasterPin(userId, inputPin) {
  try {
    const userSettingsRef = doc(db, USER_SETTINGS_COLLECTION, userId);
    const docSnap = await getDoc(userSettingsRef);
    
    if (!docSnap.exists()) {
      return false;
    }
    
    const settings = docSnap.data();
    if (!settings.masterPin || !settings.masterPin.enabled) {
      return false;
    }
    
    return await verifyPin(inputPin, settings.masterPin.hash, settings.masterPin.salt);
  } catch (error) {
    console.error('Error verifying master PIN:', error);
    return false;
  }
}

/**
 * 마스터 PIN 변경
 */
export async function changeMasterPin(userId, oldPin, newPin) {
  try {
    // 현재 PIN 검증
    const isValid = await verifyMasterPin(userId, oldPin);
    if (!isValid) {
      throw new Error('현재 PIN이 올바르지 않습니다.');
    }
    
    // 새 PIN 설정
    const { hash, salt } = await hashPin(newPin);
    
    const userSettingsRef = doc(db, USER_SETTINGS_COLLECTION, userId);
    await updateDoc(userSettingsRef, {
      'masterPin.hash': hash,
      'masterPin.salt': salt,
      'masterPin.updatedAt': new Date()
    });
    
    return true;
  } catch (error) {
    console.error('Error changing master PIN:', error);
    throw error;
  }
}

/**
 * 마스터 PIN 제거
 */
export async function removeMasterPin(userId, currentPin) {
  try {
    // 현재 PIN 검증
    const isValid = await verifyMasterPin(userId, currentPin);
    if (!isValid) {
      throw new Error('PIN이 올바르지 않습니다.');
    }
    
    const userSettingsRef = doc(db, USER_SETTINGS_COLLECTION, userId);
    await updateDoc(userSettingsRef, {
      masterPin: {
        enabled: false,
        hash: null,
        salt: null,
        updatedAt: new Date()
      }
    });
    
    // 모든 항목의 PIN 보호 플래그 제거
    await removeAllPinProtection(userId);
    
    return true;
  } catch (error) {
    console.error('Error removing master PIN:', error);
    throw error;
  }
}

/**
 * 마스터 PIN 존재 여부 확인
 */
export async function hasMasterPin(userId) {
  try {
    const userSettingsRef = doc(db, USER_SETTINGS_COLLECTION, userId);
    const docSnap = await getDoc(userSettingsRef);
    
    if (!docSnap.exists()) {
      return false;
    }
    
    const settings = docSnap.data();
    return settings.masterPin && settings.masterPin.enabled;
  } catch (error) {
    console.error('Error checking master PIN:', error);
    return false;
  }
}

/**
 * PIN 설정 가져오기
 */
export async function getPinSettings(userId) {
  try {
    const userSettingsRef = doc(db, USER_SETTINGS_COLLECTION, userId);
    const docSnap = await getDoc(userSettingsRef);
    
    if (!docSnap.exists()) {
      return {
        hasMasterPin: false,
        autoLockMinutes: 5
      };
    }
    
    const settings = docSnap.data();
    return {
      hasMasterPin: settings.masterPin?.enabled || false,
      autoLockMinutes: settings.pinSettings?.autoLockMinutes || 5
    };
  } catch (error) {
    console.error('Error getting PIN settings:', error);
    return {
      hasMasterPin: false,
      autoLockMinutes: 5
    };
  }
}

/**
 * PIN 설정 업데이트
 */
export async function updatePinSettings(userId, settings) {
  try {
    const userSettingsRef = doc(db, USER_SETTINGS_COLLECTION, userId);
    await setDoc(userSettingsRef, {
      pinSettings: {
        autoLockMinutes: settings.autoLockMinutes,
        updatedAt: new Date()
      }
    }, { merge: true });
    
    return true;
  } catch (error) {
    console.error('Error updating PIN settings:', error);
    throw error;
  }
}

/**
 * 통합 PIN 검증 (Master PIN만 사용)
 */
export async function verifyProtection(userId, inputPin) {
  try {
    // 6자리 PIN 검증
    if (inputPin.length !== 6) {
      return false;
    }
    
    return await verifyMasterPin(userId, inputPin);
  } catch (error) {
    console.error('Error verifying protection:', error);
    return false;
  }
}

/**
 * 모든 항목의 PIN 보호 플래그 제거 (마스터 PIN 제거 시)
 */
async function removeAllPinProtection(userId) {
  try {
    // SecureNotes에서 PIN 보호 제거
    const notesQuery = query(
      collection(db, SECURE_NOTES_COLLECTION),
      where('userId', '==', userId),
      where('isPinProtected', '==', true)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    const updatePromises = [];
    
    notesSnapshot.forEach((doc) => {
      updatePromises.push(
        updateDoc(doc.ref, {
          isPinProtected: false,
          updatedAt: new Date()
        })
      );
    });
    
    // SecureFiles에서 PIN 보호 제거
    const filesQuery = query(
      collection(db, SECURE_FILES_COLLECTION),
      where('userId', '==', userId),
      where('isPinProtected', '==', true)
    );
    
    const filesSnapshot = await getDocs(filesQuery);
    
    filesSnapshot.forEach((doc) => {
      updatePromises.push(
        updateDoc(doc.ref, {
          isPinProtected: false,
          updatedAt: new Date()
        })
      );
    });
    
    await Promise.all(updatePromises);
    console.log(`Removed PIN protection from ${updatePromises.length} items`);
  } catch (error) {
    console.error('Error removing all PIN protection:', error);
    // 실패해도 마스터 PIN 제거는 진행
  }
}

/**
 * 레거시 개별 PIN 데이터 마이그레이션
 * 개별 PIN이 있는 항목을 마스터 PIN 보호로 전환
 */
export async function migrateIndividualPins(userId) {
  try {
    const migrationResults = {
      notes: 0,
      files: 0,
      errors: []
    };
    
    // SecureNotes 마이그레이션
    const notesQuery = query(
      collection(db, SECURE_NOTES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    const noteUpdates = [];
    
    notesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // 개별 PIN이 있는 경우 마이그레이션
      if (data.pinHash || data.pinSalt) {
        noteUpdates.push(
          updateDoc(docSnap.ref, {
            isPinProtected: true,  // 마스터 PIN으로 보호
            pinHash: null,         // 개별 PIN 해시 제거
            pinSalt: null,         // 개별 PIN 솔트 제거
            updatedAt: new Date()
          })
        );
        migrationResults.notes++;
      }
    });
    
    // SecureFiles 마이그레이션
    const filesQuery = query(
      collection(db, SECURE_FILES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const filesSnapshot = await getDocs(filesQuery);
    const fileUpdates = [];
    
    filesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // 개별 PIN이 있는 경우 마이그레이션
      if (data.pinHash || data.pinSalt) {
        fileUpdates.push(
          updateDoc(docSnap.ref, {
            isPinProtected: true,  // 마스터 PIN으로 보호
            pinHash: null,         // 개별 PIN 해시 제거
            pinSalt: null,         // 개별 PIN 솔트 제거
            updatedAt: new Date()
          })
        );
        migrationResults.files++;
      }
    });
    
    // 모든 업데이트 실행
    await Promise.all([...noteUpdates, ...fileUpdates]);
    
    console.log('Migration completed:', migrationResults);
    return migrationResults;
  } catch (error) {
    console.error('Error migrating individual PINs:', error);
    throw error;
  }
}

export default {
  setMasterPin,
  verifyMasterPin,
  changeMasterPin,
  removeMasterPin,
  hasMasterPin,
  getPinSettings,
  updatePinSettings,
  verifyProtection,
  migrateIndividualPins
};